
import React, { useEffect, useRef } from 'react';
import { getRandomFloat } from '../utils/animations';

type LeafProps = {
  src: string;
  className?: string;
  animationClass?: string;
  style?: React.CSSProperties;
};

export const Leaf: React.FC<LeafProps> = ({ 
  src, 
  className = "", 
  animationClass = "animate-leaf-wave", 
  style 
}) => {
  return (
    <img 
      src={src} 
      alt="" 
      className={`absolute pointer-events-none select-none leaf-glow ${animationClass} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
};

type FloraElementsProps = {
  position: 'top' | 'bottom' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
  variant?: 'hero' | 'story' | 'rsvp' | 'itinerary';
};

export const FloraElements: React.FC<FloraElementsProps> = ({ 
  position, 
  variant = 'hero' 
}) => {
  const getImagePath = (index: number) => {
    return `/lovable-uploads/${index === 1 ? '2accdcd7-1d04-432b-9df1-6ca3cb437798.png' : 
           index === 2 ? '423f3590-dcc9-41b0-8eb2-98ad70f161ef.png' : 
           index === 3 ? '65c36c71-88cd-42cd-a588-135b08cd0895.png' : 
           index === 4 ? '2611d88d-2afc-4a60-ac68-f99f809cdfa9.png' : 
           index === 5 ? '475972eb-7b4a-463f-9a9c-557bcb204d43.png' : 
           index === 6 ? '3e94f599-9124-44dc-bade-c178ecb1f576.png' : 
           index === 7 ? '00124d48-8953-4da6-b080-e24a30cb0896.png' : 
           index === 8 ? '05428665-c18f-45c7-8ca7-1cb33693abd6.png' : 
           index === 9 ? 'bdf94fea-ab3c-4c83-84b2-bada2b33303e.png' : 
           index === 10 ? 'eda96774-0aa1-44b6-9889-8308530de0ca.png' : 
           'c7ef2449-1680-4e6b-987c-beadec4fbdf5.png'}`;
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'top':
        return 'top-0 left-1/2 -translate-x-1/2';
      case 'bottom':
        return 'bottom-0 left-1/2 -translate-x-1/2';
      case 'left':
        return 'left-0 top-1/2 -translate-y-1/2';
      case 'right':
        return 'right-0 top-1/2 -translate-y-1/2';
      case 'topleft':
        return 'top-0 left-0';
      case 'topright':
        return 'top-0 right-0';
      case 'bottomleft':
        return 'bottom-0 left-0';
      case 'bottomright':
        return 'bottom-0 right-0';
      default:
        return '';
    }
  };

  const getVariantConfig = () => {
    switch (variant) {
      case 'hero':
        return {
          image1: getImagePath(1),
          image2: getImagePath(2),
          size1: 'w-40 md:w-56',
          size2: 'w-32 md:w-48',
          animation1: 'animate-leaf-wave',
          animation2: 'animate-sway',
          opacity: 'opacity-70'
        };
      case 'story':
        return {
          image1: getImagePath(4),
          image2: getImagePath(5),
          size1: 'w-36 md:w-48',
          size2: 'w-28 md:w-40',
          animation1: 'animate-sway',
          animation2: 'animate-float',
          opacity: 'opacity-60'
        };
      case 'rsvp':
        return {
          image1: getImagePath(10),
          image2: getImagePath(11),
          size1: 'w-32 md:w-44',
          size2: 'w-24 md:w-36',
          animation1: 'animate-float',
          animation2: 'animate-leaf-wave',
          opacity: 'opacity-80'
        };
      case 'itinerary':
        return {
          image1: getImagePath(7),
          image2: getImagePath(8),
          size1: 'w-28 md:w-40',
          size2: 'w-20 md:w-32',
          animation1: 'animate-sway',
          animation2: 'animate-breath',
          opacity: 'opacity-70'
        };
      default:
        return {
          image1: getImagePath(1),
          image2: getImagePath(3),
          size1: 'w-32',
          size2: 'w-24',
          animation1: 'animate-float',
          animation2: 'animate-sway',
          opacity: 'opacity-80'
        };
    }
  };

  const config = getVariantConfig();
  const positionClass = getPositionStyles();

  const getRandomDelay = () => {
    return { animationDelay: `${getRandomFloat(0, 2)}s` };
  };

  return (
    <div className={`absolute ${positionClass} z-0 pointer-events-none`}>
      <Leaf 
        src={config.image1} 
        className={`${config.size1} ${config.opacity}`} 
        animationClass={config.animation1}
        style={getRandomDelay()}
      />
      <Leaf 
        src={config.image2} 
        className={`${config.size2} ${config.opacity} translate-x-1/4 -translate-y-1/4`} 
        animationClass={config.animation2}
        style={getRandomDelay()}
      />
    </div>
  );
};

export const MagnoliaDivider: React.FC = () => {
  return (
    <div className="flex items-center justify-center my-12 w-full">
      <div className="h-px bg-forest/20 w-[30%]"></div>
      <img 
        src="/lovable-uploads/423f3590-dcc9-41b0-8eb2-98ad70f161ef.png" 
        alt="" 
        className="w-20 mx-4 opacity-80" 
        aria-hidden="true"
      />
      <div className="h-px bg-forest/20 w-[30%]"></div>
    </div>
  );
};

export const FloatingLeaves: React.FC = () => {
  const leafContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const container = leafContainerRef.current;
    if (!container) return;
    
    const createLeaf = () => {
      const leaf = document.createElement('img');
      const leafType = Math.floor(getRandomFloat(1, 11));
      leaf.src = getImagePath(leafType);
      leaf.alt = '';
      leaf.style.position = 'absolute';
      leaf.style.opacity = `${getRandomFloat(0.2, 0.4)}`;
      leaf.style.width = `${getRandomFloat(20, 60)}px`;
      leaf.style.left = `${getRandomFloat(0, 100)}%`;
      leaf.style.top = `-50px`;
      leaf.style.transform = `rotate(${getRandomFloat(-20, 20)}deg)`;
      leaf.style.transition = 'top 15s linear, left 15s ease-in-out, transform 15s ease-in-out';
      leaf.style.zIndex = '-1';
      leaf.style.pointerEvents = 'none';
      
      container.appendChild(leaf);
      
      setTimeout(() => {
        leaf.style.top = `${window.innerHeight + 50}px`;
        leaf.style.left = `${parseFloat(leaf.style.left) + getRandomFloat(-20, 20)}%`;
        leaf.style.transform = `rotate(${getRandomFloat(-180, 180)}deg)`;
      }, 100);
      
      setTimeout(() => {
        if (container.contains(leaf)) {
          container.removeChild(leaf);
        }
      }, 15000);
    };
    
    const leafInterval = setInterval(createLeaf, 3000);
    
    return () => {
      clearInterval(leafInterval);
    };
  }, []);
  
  function getImagePath(index: number) {
    return `/lovable-uploads/${index === 1 ? '2accdcd7-1d04-432b-9df1-6ca3cb437798.png' : 
           index === 2 ? '423f3590-dcc9-41b0-8eb2-98ad70f161ef.png' : 
           index === 3 ? '65c36c71-88cd-42cd-a588-135b08cd0895.png' : 
           index === 4 ? '2611d88d-2afc-4a60-ac68-f99f809cdfa9.png' : 
           index === 5 ? '475972eb-7b4a-463f-9a9c-557bcb204d43.png' : 
           index === 6 ? '3e94f599-9124-44dc-bade-c178ecb1f576.png' : 
           index === 7 ? '00124d48-8953-4da6-b080-e24a30cb0896.png' : 
           index === 8 ? '05428665-c18f-45c7-8ca7-1cb33693abd6.png' : 
           index === 9 ? 'bdf94fea-ab3c-4c83-84b2-bada2b33303e.png' : 
           index === 10 ? 'eda96774-0aa1-44b6-9889-8308530de0ca.png' : 
           'c7ef2449-1680-4e6b-987c-beadec4fbdf5.png'}`;
  }
  
  return <div ref={leafContainerRef} className="fixed inset-0 w-full h-full overflow-hidden pointer-events-none" />;
};
